import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
  useMemo,
} from "react"
import { FeatureCollection } from "geojson"
import { find, pick, size, get } from "lodash"
import { useDispatch, useSelector } from "react-redux"
import uuid from "uuid/v4"

import APIs from "redux/apis"
import { APIResponse } from "redux/apis/types"
import { WorldMap } from "components/utils/chart"
import { request_geoJson } from "utils/api"
import { PAGE_NUM } from "config/const"
import { foreignSummaryTableist } from "components/pages/main/foreign/summary"
import { createRequestBit } from "utils/util"
import { changeSymbol, changeSpModalSymbol } from "redux/ducks/Chart"
import {
  indicatorChartSymbol,
  spIndicatorChartSymbol,
} from "redux/ducks/IndicatorChart"
import { LoadStateType, pageLoadRegist } from "redux/ducks/PageLoad"
import store from "redux/store"
import { checkIsSp } from "utils/util"

type UseRequestForeignIndexType = (
  tableType: number,
  effect: boolean,
  setEffect: Function
) => APIResponse.Res
export const useRequestForeignIndex: UseRequestForeignIndexType = (
  tableType,
  effect,
  setEffect
) => {
  const [data, setData] = useState<APIResponse.Res>(null)
  const type = find(foreignSummaryTableist, { id: tableType })
  const isPageLoading = useSelector(
    (state: { PageLoad: LoadStateType }) => state.PageLoad.load
  )
  const loadRegistId = useMemo(() => uuid(), [])

  const load = async () => {
    store.dispatch(pageLoadRegist(loadRegistId, true))
    const res = await APIs.SymbolDetail.PriceList({
      Name: type.api,
    })
    setData(res)
    store.dispatch(pageLoadRegist(loadRegistId, false))
  }
  useEffect(() => {
    load()
  }, [tableType])

  useEffect(() => {
    if (effect) {
      load()
      setEffect(false)
    }
  }, [effect])

  useEffect(() => {
    if (isPageLoading) load()
  }, [isPageLoading])
  return data
}

export type ForeignSummaryState = {
  selectedSymbol: number
  displayType: number
}

export type ForeignSummaryAction = {
  type: "CHANGE_SYMBOL" | "CHANGE_TABLE_TYPE"
  symbolIndex?: number
  displayType?: number
}

type ForeignSummaryReducerType = (
  state: ForeignSummaryState,
  action: ForeignSummaryAction
) => ForeignSummaryState
export const foreignReducerState: ForeignSummaryReducerType = (
  state,
  action
) => {
  switch (action.type) {
    case "CHANGE_SYMBOL":
      return { ...state, selectedSymbol: action.symbolIndex }
    case "CHANGE_TABLE_TYPE":
      if (state.displayType === action.displayType) return state
      return { selectedSymbol: 0, displayType: action.displayType }
  }
}

type DrawSummaryMapType = (
  elm: HTMLDivElement,
  data: APIResponse.Res,
  fn: Function,
  effect?: Array<string | number | boolean | APIResponse.Res>
) => void
export const useDrawSummaryMap: DrawSummaryMapType = (
  elm,
  data,
  fn,
  effect = []
) => {
  const colorSetting = useSelector(state => get(state, "Setting.color"))
  const [prevColorSetting, setPrevColor] = useState<string>(colorSetting)
  const [mapObject, setMapObj] = useState<WorldMap>(null)
  useEffect(() => {
    if (!elm || !data) return
    request_geoJson("geo")
      .then((geo: FeatureCollection) => {
        setMapObj(new WorldMap(geo, elm, data, fn, { height: 300 }))
      })
      .catch(() => {
        return
      })
  }, effect)

  useEffect(() => {
    if (
      !!mapObject &&
      !!prevColorSetting &&
      prevColorSetting !== colorSetting
    ) {
      mapObject.redraw()
      setPrevColor(colorSetting)
    }
  }, [colorSetting])
}

type useGetSymbolDetailDataType = (
  canvasId: string,
  changeTabFlag: boolean,
  setChangeTabFlag: Function
) => [APIResponse.Res, Dispatch<SetStateAction<APIResponse.Res>>]
export const useGetSymbolDetailData: useGetSymbolDetailDataType = (
  canvasId,
  changeTabFlag,
  setChangeTabFlag
) => {
  const [symbol, setSymbol] = useState<APIResponse.Res>(null)
  const [resData, setData] = useState<APIResponse.Res>(null)
  const dispatch = useDispatch()
  const loadRegistId = useMemo(() => uuid(), [])
  const isPhone = useMemo(() => {
    return checkIsSp()
  }, [])

  const load = async () => {
    if (!symbol) return
    const reqParam = pick(symbol, ["Symbol", "SecurityType", "Exchange"])
    if (!!size(reqParam)) {
      await store.dispatch(pageLoadRegist(loadRegistId, true))
      const res = await APIs.SymbolDetail.SymbolDetail(
        pick(symbol, ["Symbol", "SecurityType", "Exchange"])
      )
      !isPhone
        ? dispatch(changeSymbol(canvasId, get(res, "Symbol")))
        : !changeTabFlag && dispatch(changeSpModalSymbol(get(res, "Symbol")))
      setData(res)
      setChangeTabFlag(false)
      store.dispatch(pageLoadRegist(loadRegistId, false))
    } else {
      !isPhone
        ? dispatch(indicatorChartSymbol(canvasId, symbol))
        : dispatch(spIndicatorChartSymbol(symbol))
      setData(symbol)
    }
  }
  useEffect(() => {
    load()
  }, [symbol])

  return [resData, setSymbol]
}

//イスラエルと調整とるために?
export type calledForeignData = {
  index: APIResponse.Res
  exchange: APIResponse.Res
  interest?: APIResponse.Res
  todayIndex?: APIResponse.Res
  pastIndex?: APIResponse.Res
  futureIndex?: APIResponse.Res
}
type useCallForeignDataType = (
  code: string,
  hasInterest?: boolean,
  currentTab?: number,
  effector?: boolean, //現状booleanのみ。拡張する場合は関係処理見直し必要
  setEffector?: Function
) => calledForeignData
export const useCallForeignData: useCallForeignDataType = (
  code,
  hasInterest,
  currentTab,
  effector,
  setEffector
) => {
  const [resData, setData] = useState<calledForeignData>(null)
  const isPageLoading = useSelector(
    (state: { PageLoad: LoadStateType }) => state.PageLoad.load
  )
  const loadRegistId = useMemo(() => uuid(), [])
  const load = async () => {
    if (!!currentTab) return
    await store.dispatch(pageLoadRegist(loadRegistId, true))
    const index = await APIs.SymbolDetail.PriceList({
      Name: `FC.${code}.Index`,
    })
    const exchange = await APIs.SymbolDetail.PriceList({
      Name: `FC.${code}.FX`,
    })
    const data: calledForeignData = {
      index,
      exchange,
    }
    if (hasInterest) {
      const interest = await APIs.SymbolDetail.PriceList({
        Name: `FC.${code}.GB`,
      })
      data.interest = interest
    }
    // const todayIndex = await APIs.SymbolDetail.IndicatorList({
    //   Name: `Country.${code}.Today`,
    // })
    // const pastIndex = await APIs.SymbolDetail.IndicatorList({
    //   Name: `Country.${code}.PrevDays`,
    // })
    // const futureIndex = await APIs.SymbolDetail.IndicatorList({
    //   Name: `Country.${code}.NextDays`,
    // })
    // data.todayIndex = todayIndex
    // data.pastIndex = pastIndex
    // data.futureIndex = futureIndex
    setData(data)
    store.dispatch(pageLoadRegist(loadRegistId, false))
  }
  useEffect(() => {
    load()
  }, [])
  useEffect(() => {
    if (effector) {
      load()
      setEffector(false) //effectorが拡張されbooleanでなくなった場合修正必要
    }
  }, [effector])

  useEffect(() => {
    if (isPageLoading) load()
  }, [isPageLoading])

  return resData
}

export type calledIsraelData = {
  index: APIResponse.Res
  exchange: APIResponse.Res
}
type useCallIsraelDataType = (effector?: any) => calledIsraelData
export const useCallIsraelData: useCallIsraelDataType = effector => {
  const [resData, setData] = useState<calledIsraelData>(null)
  const isPageLoading = useSelector(
    (state: { PageLoad: LoadStateType }) => state.PageLoad.load
  )
  const loadRegistId = useMemo(() => uuid(), [])

  const load = async () => {
    await store.dispatch(pageLoadRegist(loadRegistId, true))
    const index = await APIs.SymbolDetail.PriceList({
      Name: `FC.IL.Index`,
    })

    const exchange = await APIs.SymbolDetail.PriceList({
      Name: `FC.IL.FX`,
    })
    setData({
      index,
      exchange,
    })
    store.dispatch(pageLoadRegist(loadRegistId, false))
  }

  useEffect(() => {
    load()
  }, [effector])

  useEffect(() => {
    if (isPageLoading) load()
  }, [isPageLoading])

  return resData
}

type useForeignSymbolListType = (
  symbolList: Array<{
    id: number
    name: string
    value: Array<number>
    division?: string
  }>,
  currentTab?: number,
  effector?: boolean, //現状booleanのみ。拡張する場合は関係処理見直し必要
  setEffector?: Function
) => [APIResponse.Res, Function, number, number, Function]
type ReqParam = {
  Markets: string
  _take: number
  _skip: number
  _where?: string
}
export const useForeignSymbolList: useForeignSymbolListType = (
  symbolList,
  currentTab,
  effector,
  setEffector
) => {
  const [currentPage, setPage] = useState<number>(0)
  const [resData, setData] = useState<APIResponse.Res>(null)
  const [selectList, setSelectList] = useState<number>(0)
  const isPageLoading = useSelector(
    (state: { PageLoad: LoadStateType }) => state.PageLoad.load
  )
  const loadRegistId = useMemo(() => uuid(), [])

  const load = async () => {
    if (!currentTab) return
    await store.dispatch(pageLoadRegist(loadRegistId, true))
    const reqParams: ReqParam = {
      Markets: createRequestBit(symbolList[selectList].value, 21),
      _take: PAGE_NUM.PAGE_20,
      _skip: PAGE_NUM.PAGE_20 * currentPage,
    }
    if (!!symbolList[selectList].division) {
      reqParams._where = `Division="${symbolList[selectList].division}"`
    }
    const data = await APIs.SymbolDetail.EquitySymbolList(reqParams)
    setData(data)
    store.dispatch(pageLoadRegist(loadRegistId, false))
  }

  useEffect(() => {
    load()
  }, [selectList, currentPage])
  useEffect(() => {
    if (effector) {
      load()
      setEffector(false) //effectorが拡張されbooleanでなくなった場合修正必要
    }
  }, [effector])

  useEffect(() => {
    if (isPageLoading) load()
  }, [isPageLoading])

  return [resData, setPage, currentPage, selectList, setSelectList]
}

type useIsraelSymbolListType = (
  symbol: { securityType: number; exchange: Array<number> },
  effector?: any
) => [APIResponse.Res, Function, number]
export const useIsraelSymbolList: useIsraelSymbolListType = (
  symbol,
  effector
) => {
  const [currentPage, setPage] = useState<number>(0)
  const [resData, setData] = useState<APIResponse.Res>(null)
  const isPageLoading = useSelector(
    (state: { PageLoad: LoadStateType }) => state.PageLoad.load
  )

  const loadRegistId = useMemo(() => uuid(), [])

  const load = async () => {
    await store.dispatch(pageLoadRegist(loadRegistId, true))
    const data = await APIs.SymbolDetail.SymbolList({
      TargetSecurityTypes: symbol.securityType,
      Exchange: symbol.exchange[0],
      _take: PAGE_NUM.PAGE_20,
      _skip: PAGE_NUM.PAGE_20 * currentPage,
    })
    setData(data)
    await store.dispatch(pageLoadRegist(loadRegistId, false))
  }
  useEffect(() => {
    load()
  }, [currentPage])

  useEffect(() => {
    if (isPageLoading) load()
  }, [isPageLoading])
  return [resData, setPage, currentPage]
}
